import Link from 'next/link';
import { useMemo } from 'react';
import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { NearbySiteSummary } from 'components/SiteMaster/types';
import {
  StSiteMasterNearbyCard,
  StSiteMasterSection,
  StSiteMasterSectionTitle,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getSiteDetailUrlFromCoveo,
  getFieldValue,
  useSiteMasterRouteFields,
} from 'components/SiteMaster/utils';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import { SiteSearchResultRaw } from 'lib/types/coveo/SiteSearchResult';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';

type NearBySiteLocationProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    viewSiteButtonText?: Field<string>;
    siteFetchCount?: Field<number>;
  };
};

const mapCoveoResultToNearbySite = (raw: SiteSearchResultRaw): NearbySiteSummary => ({
  id: raw.identifiableuri,
  name: raw.site_name,
  url: getSiteDetailUrlFromCoveo(raw),
  city: raw.site_city,
  state: raw.site_state || raw.site_statecode,
  distanceMiles: raw.distance ?? 0,
});

const NearBySiteLocation = (props: NearBySiteLocationProps): JSX.Element => {
  const routeFields = useSiteMasterRouteFields();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const latitude = Number(getFieldValue(routeFields.siteLatitude));
  const longitude = Number(getFieldValue(routeFields.siteLongitude));
  const siteName = getFieldValue(routeFields.siteName);
  const radiusMiles = Number(siteSettings?.siteRadius?.value ?? 50);
  const fetchCount = Number(
    props.fields.siteFetchCount?.value ?? siteSettings?.nearBySiteFetchCount?.value ?? 4
  );

  const { state } = useSitesResultList(sitesHeadlessEngine, {
    latitude,
    longitude,
    distanceMiles: radiusMiles,
    sortByDistance: true,
    resultsPerPage: fetchCount + 1,
  });

  const nearbySites = useMemo(
    () =>
      (state?.results ?? [])
        .map((result) => mapCoveoResultToNearbySite(result.raw as unknown as SiteSearchResultRaw))
        .filter((site) => site.name !== siteName)
        .slice(0, fetchCount),
    [state?.results, siteName, fetchCount]
  );

  return (
    <StSiteMasterSection>
      <StSiteMasterSectionTitle>
        <Text field={props.fields.sectionTitle} />
      </StSiteMasterSectionTitle>
      {nearbySites.length ? (
        nearbySites.map((site) => (
          <StSiteMasterNearbyCard key={site.id}>
            <div>
              <strong>{site.name}</strong>
              <div>{[site.city, site.state].filter(Boolean).join(', ')}</div>
              <div>{site.distanceMiles.toFixed(1)} mi</div>
            </div>
            <Link passHref href={site.url}>
              <Text field={props.fields.viewSiteButtonText} />
            </Link>
          </StSiteMasterNearbyCard>
        ))
      ) : (
        <p>No nearby locations found.</p>
      )}
    </StSiteMasterSection>
  );
};

export default NearBySiteLocation;
